import React from 'react';
import { Box, Button, Typography } from '@heycater/design-system';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { findCitySlug } from 'shared/helpers/cities';
import { marketplaceURL } from 'shared/helpers/urls';
import { useCurrencySymbol } from 'shared/hooks/useCurrencySymbol';
import { useLocalizedCity } from 'shared/hooks/useLocalizeCity';
import useLocalizedFieldName from 'shared/hooks/useLocalizedFieldName';
import Delivery from 'shared/svg/Delivery';
import LeadTime from 'shared/svg/LeadTime';
import QualityAndSafetyIcon from 'shared/svg/QualityAndSafetyIcon';
import Rating from 'static_pages/marketing_pages/components/TopCaterers/Rating';

const CARD_WIDTH = 280;

export default function TopCaterersCard({ caterer, city: providedCity }) {
  const { t } = useTranslation('static_pages');
  const { localizedFieldAlgolia } = useLocalizedFieldName();

  const city = providedCity || caterer.cities[0];
  const localizedCity = useLocalizedCity(city);

  const url = marketplaceURL(`/catering/${findCitySlug(city)}/${caterer.slug}`);
  const currencySymbol = useCurrencySymbol();

  return (
    <StyledLink href={url} target="_blank">
      <StyledTopCaterersCard as="article">
        <Box position="relative">
          <ImageWrapper>
            {caterer.profile_image && (
              <Image
                alt=""
                height={200}
                width={CARD_WIDTH}
                src={caterer.profile_image}
                objectFit="cover"
              />
            )}
          </ImageWrapper>
          <StyledPill>
            <Typography lineHeight={1} variant="caption">
              {localizedCity}
            </Typography>
          </StyledPill>
        </Box>

        <CatererInformationWrapper>
          <CatererInformation>
            <CardTitleTypography>{caterer.company_name}</CardTitleTypography>
          </CatererInformation>
          <SloganTypography variant="body2">
            {caterer[localizedFieldAlgolia('slogan')]}
          </SloganTypography>
          <Box display="flex" alignItems="center" my={1.5} mb={2}>
            {caterer.quality_safety_rating === 'Quality Proven Partner' && (
              <>
                <QualityAndSafetyIcon fontSize="small" />
                <Divider />
              </>
            )}

            <Rating score={caterer.rating_score} isRated={caterer.is_rated} />
          </Box>
          <DeliverInfoContainer display="inline" alignItems="center">
            <DeliveryBox
              display="inline-flex"
              fontSize={18}
              ml={0.2}
              alignItems="center"
            >
              <Delivery htmlColor="#537124" fontSize="inherit" />
              <InfoTypography variant="caption" fontSize={12} ml={1} mr={1.5}>
                {caterer.minimum_delivery_fee === 0
                  ? t('catererCards.freeDelivery', {
                      amount: wholeNumberWithCurrencySymbol(
                        caterer.minimum_delivery_fee,
                        currencySymbol
                      ),
                    })
                  : t('catererCards.delivery', {
                      amount: wholeNumberWithCurrencySymbol(
                        caterer.minimum_delivery_fee,
                        currencySymbol
                      ),
                    })}
              </InfoTypography>
            </DeliveryBox>

            {caterer.numeric_lead_time && (
              <Box display="inline-flex" alignItems="center" fontSize={18}>
                <LeadTime htmlColor="#537124" fontSize={'inherit'} />
                <InfoTypography variant="caption" ml={1}>
                  {t('catererCards.leadTime', {
                    days: caterer.numeric_lead_time,
                    count: caterer.numeric_lead_time,
                  })}
                </InfoTypography>
              </Box>
            )}
          </DeliverInfoContainer>
        </CatererInformationWrapper>

        <CatererFooter>
          <ButtonLink
            size="lg"
            rounded={false}
            variant="tertiary"
            className="btn-caterer"
          >
            <span className="btn-caterer__copy">
              {t('catererCards.seeCaterer')}
            </span>
            <ChevronRightIcon className="btn-caterer__icon" />
          </ButtonLink>
        </CatererFooter>
      </StyledTopCaterersCard>
    </StyledLink>
  );
}

function wholeNumberWithCurrencySymbol(number, currencySymbol) {
  return `${Math.floor(number / 100)}${currencySymbol}`;
}

const InfoTypography = styled(Typography)`
  color: ${({ theme }) => theme.palette.grey.secondary};
`;

const StyledTopCaterersCard = styled.div`
  width: ${CARD_WIDTH}px;
  height: 100%;
  display: flex;
  flex-direction: column;

  box-shadow: 0px 4px 40px rgba(62, 41, 29, 0.08);
  border-radius: ${(props) => props.theme.spacing(1.5)}px;

  img {
    width: 100%;
    display: block;
    position: relative;
    transition: transform 0.5s ease, filter 0.5s ease;
  }

  &:hover img {
    filter: brightness(0.85);
    transform: scale(1.1);
  }

  overflow: hidden;
`;

const StyledLink = styled.a`
  text-decoration: inherit;
  color: inherit;
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover .btn-caterer {
    background-color: ${({ theme }) => theme.palette.grey[600]};
    color: ${({ theme }) => theme.palette.common.white};
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  min-height: 200px;
`;

const CatererInformation = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${(props) => props.theme.spacing(2)}px;
`;

const CatererInformationWrapper = styled.div`
  flex-grow: 1;
  padding: ${(props) => props.theme.spacing(0, 2)};
  min-height: 145px;
  ${(props) => props.theme.breakpoints.down('md')} {
    min-height: 160px;
  }
  ${(props) => props.theme.breakpoints.down('xs')} {
    min-height: fit-content;
  }
`;

const TruncatedTypography = styled(Typography)`
  white-space: nowrap;
  position: relative;
  width: 100%;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: 40px;
    background: linear-gradient(to right, rgba(255, 255, 255, 0), #fff);
  }
`;

const CardTitleTypography = styled((props) => (
  <TruncatedTypography {...props} variant="h4" component="h3" fontSize="18px" />
))`
  color: black;
  font-weight: 600;
  font-size: 18px;
  line-height: 172.5%;
`;

const Divider = styled.div`
  border: 1px solid ${(props) => props.theme.palette.grey[400]};
  margin: ${(props) => props.theme.spacing(0, 2, 0, 1.5)};
  height: 18px;
  width: 0px;
`;

const ButtonLink = styled(Button)`
  width: 100%;
  height: 42px;
  border-radius: 0px;
  font-size: 14px;

  & > .btn-caterer__icon {
    position: relative;
    transition: ${(props) =>
      props.theme.transitions.create('right', { duration: '250ms' })};
    right: 0;
  }
`;

const DeliverInfoContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: ${({ theme }) => theme.spacing(1)}px;

  ${(props) => props.theme.breakpoints.down('md')} {
    display: flex;
    align-items: start;
    flex-direction: column;
  }
  ${(props) => props.theme.breakpoints.down('xs')} {
    display: inline;
    align-items: center;
  }
`;

const DeliveryBox = styled(Box)`
  ${(props) => props.theme.breakpoints.down('md')} {
    margin-bottom: ${(props) => props.theme.spacing(0.5)}px;
  }
  ${(props) => props.theme.breakpoints.down('xs')} {
    margin-bottom: ${(props) => props.theme.spacing(0)}px;
  }
`;

const CatererFooter = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: ${({ theme }) => theme.spacing(2)}px;
`;

const SloganTypography = styled(TruncatedTypography)`
  font-size: 14px;
  min-height: ${(props) => props.theme.spacing(1)}px;
  margin-top: ${(props) => props.theme.spacing(0.5)}px;
`;

const StyledPill = styled(Box)`
  position: absolute;
  top: ${(props) => props.theme.spacing(1)}px;
  right: ${(props) => props.theme.spacing(1)}px;
  padding: ${(props) => props.theme.spacing(0.5, 1)};
  height: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f5f5f5;
  border-radius: ${(props) => props.theme.spacing(8)}px;
`;
